import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { axiosInstance } from '../../axios';
import { AdCreativeKind, AdCreativeListItem, AdCreativesApiResponse } from './types';

export const AD_CREATIVES_QUERY_KEY = ['ad-creatives'] as const;

export const adCreativesKey = (kind?: AdCreativeKind): readonly unknown[] => (
  kind ? [...AD_CREATIVES_QUERY_KEY, kind] : AD_CREATIVES_QUERY_KEY
);

/**
 * Katalog kreacji i szablonów. Lista nie zawiera sceny — backend oddaje ją
 * dopiero przy otwarciu konkretnego wpisu, bo JSON planszy jest najcięższą
 * częścią dokumentu, a katalog pokazuje wyłącznie miniatury.
 */
export const fetchAdCreatives = async (kind?: AdCreativeKind): Promise<AdCreativeListItem[]> => {
  const res = await axiosInstance.get<AdCreativesApiResponse<AdCreativeListItem[]>>(
    '/v3-getAdCreatives',
    { params: kind ? { kind } : undefined },
  );
  const data = res.data?.data;
  // Zimny start funkcji potrafi zwrócić odpowiedź o innym kształcie (echo
  // warmupu) — rzucamy, zamiast cache'ować pustą listę.
  if (!Array.isArray(data)) {
    throw new Error('Nieoczekiwany kształt odpowiedzi /v3-getAdCreatives');
  }
  return data;
};

/**
 * Biblioteka zmienia się wyłącznie przez nasze własne mutacje, a panel montuje
 * się na nowo przy każdym przełączeniu narzędzia w edytorze. Bez `staleTime`
 * każde wejście w zakładkę Szablony budziło zimną funkcję po nic, więc lista
 * jest świeża „na zawsze": odświeża ją unieważnienie po zapisie lub usunięciu
 * albo przycisk w panelu (`refetch`).
 */
export const useGetAdCreatives = (
  kind?: AdCreativeKind,
): UseQueryResult<AdCreativeListItem[], AxiosError> => useQuery<AdCreativeListItem[], AxiosError>({
  queryKey: adCreativesKey(kind),
  queryFn: () => fetchAdCreatives(kind),
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
});
